/**
 * passkeyService.js — WebAuthn / Passkeys (FIDO2)
 *
 * Abstrahiert den vollständigen Passkey-Flow für Registrierung und Login.
 * Nutzt @simplewebauthn/browser (lazy import → kein Webpack-Overhead wenn
 * der Browser WebAuthn nicht unterstützt).
 *
 * Registrierung (nur für eingeloggte User — in den Einstellungen):
 *   await passkeyService.register("iPhone 15")
 *
 * Authentication (auf dem Login-Screen):
 *   const token = await passkeyService.authenticate("user@example.com")
 *
 * Die API-Endpunkte:
 *   GET  /auth/passkey/register/options        (auth required)
 *   POST /auth/passkey/register/verify         (auth required)
 *   POST /auth/passkey/authenticate/options    (public)
 *   POST /auth/passkey/authenticate/verify     (public)
 *   GET  /auth/passkey                         (auth required)
 *   DELETE /auth/passkey/:credentialId         (auth required)
 */

import api from '@/services/axios';

// Lazy-Import der WebAuthn-Browser-Library
async function getWebAuthn() {
  const mod = await import('@simplewebauthn/browser');
  return { startRegistration: mod.startRegistration, startAuthentication: mod.startAuthentication };
}

export const passkeyService = {

  /**
   * Prüft ob der Browser / das Gerät einen Plattform-Authenticator hat
   * (Face ID, Touch ID, Windows Hello…).
   * Gecacht nach erstem Aufruf.
   * @returns {Promise<boolean>}
   */
  async isSupported() {
    if (this._supported !== undefined) return this._supported;
    try {
      if (typeof window === 'undefined' || !window.PublicKeyCredential) {
        this._supported = false;
        return false;
      }
      this._supported =
        await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch {
      this._supported = false;
    }
    return this._supported;
  },

  /**
   * Neuen Passkey registrieren (nur für eingeloggten User).
   * Löst den Face ID / Touch ID Dialog aus.
   *
   * @param {string} [name=''] — Anzeigename für diesen Passkey (z.B. "iPhone 15")
   * @throws {Error} wenn der User abbricht oder der Browser die Registrierung ablehnt
   */
  async register(name = '') {
    const { startRegistration } = await getWebAuthn();

    // 1) Challenge + Optionen vom Server holen
    const { data: options } = await api.get('/auth/passkey/register/options');

    // 2) Browser-Dialog (Face ID / Touch ID)
    // Ab @simplewebauthn/browser v9: Options müssen als { optionsJSON } übergeben werden
    const credential = await startRegistration({ optionsJSON: options });

    // 3) Verifizieren und in DB speichern
    await api.post('/auth/passkey/register/verify', { credential, name });
  },

  /**
   * Login via Passkey.
   * Wenn email angegeben → Server schränkt auf Passkeys dieses Users ein.
   * Ohne email → discoverable credential (Browser zeigt eigenen Picker).
   *
   * @param {string} [email=''] — Optional: E-Mail für gezielte Passkey-Auswahl
   * @returns {Promise<string>} JWT-Token
   * @throws {Error} wenn der User abbricht oder die Verifikation fehlschlägt
   */
  async authenticate(email = '') {
    const { startAuthentication } = await getWebAuthn();

    // 1) Challenge + Optionen vom Server holen
    const { data } = await api.post('/auth/passkey/authenticate/options', { email: email || null });
    const { options, challengeToken } = data;

    // 2) Browser-Dialog (Face ID / Touch ID)
    // Ab @simplewebauthn/browser v9: Options müssen als { optionsJSON } übergeben werden
    const credential = await startAuthentication({ optionsJSON: options });

    // 3) Verifizieren → JWT
    const { data: result } = await api.post('/auth/passkey/authenticate/verify', {
      challengeToken,
      credential,
    });

    return result.token;
  },

  /**
   * Alle eigenen Passkeys auflisten.
   * @returns {Promise<Array>}
   */
  async list() {
    const { data } = await api.get('/auth/passkey');
    return data.passkeys || [];
  },

  /**
   * Passkey löschen.
   * @param {string} credentialId — interne DB-ID des Passkeys
   */
  async delete(credentialId) {
    await api.delete(`/auth/passkey/${encodeURIComponent(credentialId)}`);
  },

  // Interner Cache für isSupported()
  _supported: undefined,
};
